// Mobile shell — compact top bar + scrollable routed page + bottom tab bar.
// Every phone, in either orientation (useIsMobile); desktop gets Layout.tsx's
// TopNav + page/chat split.
import { Moon, Monitor, Sun } from 'lucide-react';
import { Outlet } from 'react-router-dom';
import { Button } from '@onestone/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@onestone/ui/dropdown-menu';
import { useTheme } from '@/components/ThemeProvider';
import { InstallAppBanner } from '@/components/InstallAppBanner';
import { UpdateBanner } from '@/components/UpdateBanner';
import { SWUpdateToast } from '@/components/SWUpdateToast';
import { AccountMenu } from './AccountMenu';
import { ActiveProjectIndicator } from './ActiveProjectIndicator';
import { RestartButton } from './RestartButton';
import { BottomTabBar } from './BottomTabBar';
import { SkipToMainContent } from './SkipToMainContent';

export function MobileLayout() {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const ThemeIcon = resolvedTheme === 'dark' ? Moon : Sun;

  return (
    <div data-testid="layout-mobile" className="flex h-full min-h-0 flex-col bg-background text-foreground">
      <SkipToMainContent />
      <header
        data-testid="mobile-header"
        className="flex h-12 shrink-0 items-center justify-between border-b border-border px-3"
      >
        <div className="flex min-w-0 items-center gap-2">
          <img src="./logo-256.png" alt="OneStone" className="h-7 w-7 shrink-0 rounded-lg shadow-sm" />
          <span className="text-sm font-bold tracking-tight">OneStoneAI</span>
        </div>
        <div className="flex min-w-0 items-center gap-1">
          <ActiveProjectIndicator />
          <div className="mx-0.5 h-5 w-px bg-border" aria-hidden="true" />
          <RestartButton />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label="Toggle theme">
                <ThemeIcon className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Theme</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setTheme('light')}>
                <Sun className="mr-2 h-4 w-4" /> Light
                {theme === 'light' && <span className="ml-auto text-xs">●</span>}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setTheme('dark')}>
                <Moon className="mr-2 h-4 w-4" /> Dark
                {theme === 'dark' && <span className="ml-auto text-xs">●</span>}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setTheme('system')}>
                <Monitor className="mr-2 h-4 w-4" /> System
                {theme === 'system' && <span className="ml-auto text-xs">●</span>}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {/* Account chip — cloud host only (null account → renders nothing) */}
          <AccountMenu />
        </div>
      </header>

      <InstallAppBanner />
      <UpdateBanner />
      <SWUpdateToast />

      <main
        id="main-content"
        data-testid="main-content"
        tabIndex={-1}
        className="flex min-h-0 flex-1 flex-col overflow-y-auto focus:outline-none"
      >
        <Outlet />
      </main>

      <BottomTabBar />
    </div>
  );
}
